import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { formatCurrency } from '../../../utils/formatters';
import type { MarcaTarjeta, MedioTransferencia } from '../../../services/tesoreriaService';
import type { POSCobroDraft, POSLineaCobro, POSMedioCobroTipo } from './types';
import { FORMAS_DE_PAGO } from './constants';
import SearchableSelect from '../../../components/SearchableSelect';

export interface FacturaPOSPagoPanelProps {
  total: number;
  clienteSeleccionado: boolean;
  formaDePago: string;
  onFormaDePagoChange: (v: string) => void;
  cobroHabilitado: boolean;
  cobroRequerido: boolean;
  /** Tras intentar emitir sin completar medios de cobro. */
  mostrarCobroIncompleto?: boolean;
  readOnly?: boolean;
  loadingGrupo?: boolean;
  loadingMedios?: boolean;
  tieneMedios: boolean;
  grupoNombre?: string;
  /** Más de un grupo MCP asignado al usuario. */
  puedeCambiarGrupo?: boolean;
  onCambiarGrupo?: () => void;
  lineasCobro: POSLineaCobro[];
  onEliminarLinea: (id: string) => void;
  sumaCobrada: number;
  restante: number;
  draft: POSCobroDraft;
  onDraftTipoChange: (t: POSMedioCobroTipo) => void;
  onDraftFieldChange: <K extends keyof POSCobroDraft>(key: K, value: POSCobroDraft[K]) => void;
  onAgregarMedio: () => string | null;
  puedeAgregarMedio: boolean;
  mediosEfectivo: MedioTransferencia[];
  mediosTarjeta: MedioTransferencia[];
  mediosTransferencia: MedioTransferencia[];
  mediosOtros: MedioTransferencia[];
  marcasTarjeta: MarcaTarjeta[];
  /** Control opcional del acordeón (p. ej. atajo de cobro). */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const labelMedio = (m: MedioTransferencia): string =>
  (m.descripcion || m.nombre || m.mcp || 'Medio').trim();

const labelMedioMcp = (m: MedioTransferencia): string => {
  const mcp = String(m.mcp ?? '').trim();
  const detalle = labelMedio(m);
  if (mcp && detalle && mcp !== detalle) return `${mcp} · ${detalle}`;
  return mcp || detalle;
};

const tipoLabel: Record<POSMedioCobroTipo, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  transferencia: 'Transferencia',
  otros: 'Otros',
};

const FacturaPOSPagoPanel: React.FC<FacturaPOSPagoPanelProps> = ({
  total,
  clienteSeleccionado,
  formaDePago,
  onFormaDePagoChange,
  cobroHabilitado,
  cobroRequerido,
  mostrarCobroIncompleto = false,
  readOnly = false,
  loadingGrupo = false,
  loadingMedios = false,
  tieneMedios,
  grupoNombre,
  puedeCambiarGrupo = false,
  onCambiarGrupo,
  lineasCobro,
  onEliminarLinea,
  sumaCobrada,
  restante,
  draft,
  onDraftTipoChange,
  onDraftFieldChange,
  onAgregarMedio,
  puedeAgregarMedio,
  mediosEfectivo,
  mediosTarjeta,
  mediosTransferencia,
  mediosOtros,
  marcasTarjeta,
  expanded: expandedProp,
  onExpandedChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [expandedInternal, setExpandedInternal] = useState(false);
  const expanded = expandedProp ?? expandedInternal;
  const setExpanded = (exp: boolean) => {
    onExpandedChange?.(exp);
    if (expandedProp === undefined) setExpandedInternal(exp);
  };

  const seleccionarTodoElInput = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const el = e.target;
    requestAnimationFrame(() => el.select());
  };

  useEffect(() => {
    if (mostrarCobroIncompleto) setExpanded(true);
  }, [mostrarCobroIncompleto]);

  const puedeConfigurar = clienteSeleccionado && total > 0;
  const deshabilitado = readOnly || !puedeConfigurar || !cobroHabilitado;
  const cobroOpcional = cobroHabilitado && !cobroRequerido;

  const muestraEfectivo = mediosEfectivo.length > 0;
  const muestraTarjeta = mediosTarjeta.length > 0;
  const muestraTransferencia = mediosTransferencia.length > 0;
  const muestraOtros = mediosOtros.length > 0;

  const cobroCompleto = restante <= 0.01;

  const tiposMedioChip: { value: POSMedioCobroTipo; label: string }[] = [
    ...(muestraEfectivo ? [{ value: 'efectivo' as const, label: 'Efectivo' }] : []),
    ...(muestraTarjeta ? [{ value: 'tarjeta' as const, label: 'Tarjeta' }] : []),
    ...(muestraTransferencia
      ? [{ value: 'transferencia' as const, label: 'Transferencia' }]
      : []),
    ...(muestraOtros ? [{ value: 'otros' as const, label: 'Otros' }] : []),
  ];

  const handleAgregar = () => {
    const err = onAgregarMedio();
    if (err) enqueueSnackbar(err, { variant: 'warning' });
  };

  const renderSelectMedioCobro = (
    medios: MedioTransferencia[],
    selectedIdx: number | string | null,
    field: 'medioEfectivoId' | 'medioTarjetaId' | 'medioOtrosId',
  ) => {
    if (medios.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No hay medios de cobro disponibles para este tipo.
        </Typography>
      );
    }
    const idx =
      selectedIdx != null &&
      Number(selectedIdx) >= 0 &&
      Number(selectedIdx) < medios.length
        ? Number(selectedIdx)
        : 0;
    return (
      <SearchableSelect<number>
        label="Medio de cobro"
        size="small"
        value={idx}
        onChange={(v) => onDraftFieldChange(field, v ?? 0)}
        options={medios.map((m, i) => ({
          value: i,
          label: labelMedioMcp(m),
        }))}
      />
    );
  };

  const campoImporte = (
    <TextField
      label="Importe"
      size="small"
      fullWidth
      type="number"
      value={draft.importe > 0 ? draft.importe : ''}
      onChange={(e) => {
        const v = parseFloat(String(e.target.value).replace(',', '.'));
        onDraftFieldChange('importe', Number.isNaN(v) ? 0 : v);
      }}
      inputProps={{
        min: 0,
        step: 0.01,
        onFocus: seleccionarTodoElInput,
      }}
      sx={{
        '& .MuiOutlinedInput-root': { borderRadius: 1 },
        '& .MuiInputBase-input': { textAlign: 'right' },
      }}
    />
  );

  const filaImporteYAgregar = (
    <Stack direction="row" spacing={1} alignItems="stretch" sx={{ width: '100%' }}>
      <Box sx={{ flex: '1 1 0', width: 0, minWidth: 0 }}>{campoImporte}</Box>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAgregar}
        sx={{
          flex: '1 1 0',
          width: 0,
          minHeight: 40,
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Agregar medio
      </Button>
    </Stack>
  );

  return (
    <Accordion
      id="pos-pago-panel"
      expanded={expanded}
      onChange={(_, exp) => setExpanded(exp)}
      variant="outlined"
      disableGutters
      sx={{
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        ...(cobroRequerido ? { borderColor: 'warning.light', borderWidth: 1 } : {}),
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <PaymentIcon fontSize="small" color={cobroRequerido ? 'warning' : 'action'} />
          <Typography variant="body2">Pago</Typography>
          {mostrarCobroIncompleto && cobroRequerido && (
            <Chip label="Cobro requerido" size="small" color="warning" variant="outlined" />
          )}
          {lineasCobro.length > 1 && (
            <Chip label={`${lineasCobro.length} medios`} size="small" variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {!clienteSeleccionado || total <= 0 ? (
          <Typography variant="body2" color="text.secondary">
            Agregá artículos y cliente para configurar el pago.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {puedeCambiarGrupo && cobroHabilitado && (
              <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap>
                {grupoNombre ? (
                  <Typography variant="caption" color="text.secondary">
                    Grupo de caja:{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {grupoNombre}
                    </Box>
                  </Typography>
                ) : (
                  <Typography variant="caption" color="warning.main">
                    Sin grupo de caja seleccionado
                  </Typography>
                )}
                {onCambiarGrupo && !readOnly && (
                  <IconButton
                    size="small"
                    onClick={onCambiarGrupo}
                    disabled={loadingGrupo}
                    aria-label="Cambiar grupo de caja"
                    sx={{ p: 0.25 }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            )}
            {cobroHabilitado && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 0.25 }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Total a cobrar
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  {formatCurrency(total)}
                </Typography>
              </Stack>
            )}

            {cobroOpcional && (
              <Typography variant="caption" color="text.secondary">
                El cobro es opcional; podés emitir sin cargar medios.
              </Typography>
            )}

            <SearchableSelect
              label="Forma de pago"
              size="small"
              disabled={readOnly}
              allowEmpty
              emptyOptionLabel="Seleccionar"
              value={formaDePago || null}
              onChange={(v) => onFormaDePagoChange(v ?? '')}
              options={FORMAS_DE_PAGO.map((f) => ({ value: f, label: f }))}
            />

            {cobroHabilitado && (
              <>
            {loadingGrupo || loadingMedios ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : !tieneMedios ? (
              <Typography variant="body2" color="error">
                No hay medios de cobro en el grupo de caja
                {grupoNombre ? ` (${grupoNombre})` : ''}. Configurá el grupo del usuario.
              </Typography>
            ) : (
              <>
                {(lineasCobro.length === 0 || puedeAgregarMedio) && !deshabilitado && (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {lineasCobro.length === 0 ? 'Agregar medio de cobro' : 'Agregar otro medio'}
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                      {tiposMedioChip.map(({ value, label }) => {
                        const selected = draft.tipo === value;
                        return (
                          <Chip
                            key={value}
                            label={label}
                            clickable={!deshabilitado}
                            disabled={deshabilitado}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => onDraftTipoChange(value)}
                            sx={{
                              fontWeight: selected ? 600 : 500,
                              borderRadius: 2,
                              ...(selected
                                ? {}
                                : {
                                    bgcolor: 'background.paper',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                  }),
                            }}
                          />
                        );
                      })}
                    </Stack>

                    {draft.tipo === 'efectivo' && (
                      <Stack spacing={1}>
                        {renderSelectMedioCobro(
                          mediosEfectivo,
                          draft.medioEfectivoId,
                          'medioEfectivoId',
                        )}
                        {filaImporteYAgregar}
                      </Stack>
                    )}

                    {draft.tipo === 'tarjeta' && (
                      <Stack spacing={1}>
                        {renderSelectMedioCobro(
                          mediosTarjeta,
                          draft.medioTarjetaId,
                          'medioTarjetaId',
                        )}
                        <SearchableSelect<number>
                          label="Marca"
                          size="small"
                          allowEmpty
                          emptyOptionLabel="Seleccionar"
                          value={draft.tarjeta.codigoMarca > 0 ? draft.tarjeta.codigoMarca : null}
                          onChange={(v) =>
                            onDraftFieldChange('tarjeta', {
                              ...draft.tarjeta,
                              codigoMarca: v ?? 0,
                            })
                          }
                          options={marcasTarjeta.map((marca) => ({
                            value: marca.codigoMarca,
                            label: marca.nombreMarca || marca.nombre,
                          }))}
                        />
                        <TextField
                          label="Nº autorización"
                          size="small"
                          fullWidth
                          value={draft.tarjeta.numeroroAutorizacion}
                          onChange={(e) =>
                            onDraftFieldChange('tarjeta', {
                              ...draft.tarjeta,
                              numeroroAutorizacion: e.target.value,
                            })
                          }
                        />
                        <TextField
                          label="Cuotas"
                          size="small"
                          type="number"
                          fullWidth
                          inputProps={{ min: 1, step: 1 }}
                          value={draft.tarjeta.cuotas ?? 1}
                          onChange={(e) =>
                            onDraftFieldChange('tarjeta', {
                              ...draft.tarjeta,
                              cuotas: Math.max(1, Number(e.target.value) || 1),
                            })
                          }
                        />
                        {filaImporteYAgregar}
                      </Stack>
                    )}

                    {draft.tipo === 'transferencia' && (
                      <Stack spacing={1}>
                        <SearchableSelect<number>
                          label="Cuenta / transferencia"
                          size="small"
                          value={
                            draft.medioTransferenciaId != null &&
                              Number(draft.medioTransferenciaId) >= 0 &&
                              Number(draft.medioTransferenciaId) < mediosTransferencia.length
                              ? Number(draft.medioTransferenciaId)
                              : 0
                          }
                          onChange={(idx) => {
                            onDraftFieldChange(
                              'medioTransferenciaId',
                              idx != null && Number.isFinite(idx) ? idx : 0,
                            );
                          }}
                          options={mediosTransferencia.map((m, idx) => ({
                            value: idx,
                            label: `${labelMedio(m)}${m.nombreBanco ? ` · ${m.nombreBanco}` : ''}`,
                          }))}
                        />
                        {filaImporteYAgregar}
                      </Stack>
                    )}

                    {draft.tipo === 'otros' && (
                      <Stack spacing={1}>
                        {renderSelectMedioCobro(
                          mediosOtros,
                          draft.medioOtrosId,
                          'medioOtrosId',
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            fullWidth
                            label="Fecha de registro (opcional)"
                            type="date"
                            value={draft.fechaRegistro || ''}
                            onChange={(e) => onDraftFieldChange('fechaRegistro', e.target.value)}
                            disabled={deshabilitado}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                          <TextField
                            fullWidth
                            label="Número de comprobante (opcional)"
                            type="number"
                            value={
                              draft.nroComprobante != null && draft.nroComprobante > 0
                                ? draft.nroComprobante
                                : ''
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              onDraftFieldChange(
                                'nroComprobante',
                                v === '' ? undefined : Math.trunc(Number(v) || 0),
                              );
                            }}
                            disabled={deshabilitado}
                            inputProps={{ min: 1, step: 1 }}
                            size="small"
                          />
                        </Stack>
                        {filaImporteYAgregar}
                      </Stack>
                    )}
                  </>
                )}

                {lineasCobro.length > 0 && (
                  <Stack spacing={0.75}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Medios agregados
                    </Typography>
                    {lineasCobro.map((linea) => (
                      <Stack
                        key={linea.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 0.75,
                          px: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap fontWeight={500}>
                            {tipoLabel[linea.tipo]} · {linea.label}
                          </Typography>
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            {formatCurrency(linea.importe)}
                          </Typography>
                          {linea.tipo === 'otros' && !!linea.fechaRegistro && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Fecha registro:{' '}
                              {new Date(`${linea.fechaRegistro}T12:00:00`).toLocaleDateString('es-AR')}
                            </Typography>
                          )}
                          {linea.tipo === 'otros' &&
                            linea.nroComprobante != null &&
                            linea.nroComprobante > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Nº comprobante: {linea.nroComprobante}
                            </Typography>
                          )}
                        </Box>
                        {!deshabilitado && (
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Quitar medio"
                            onClick={() => onEliminarLinea(linea.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    py: 0.75,
                    px: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h6" color="primary" fontWeight={700}>
                    Cobrado
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight={700}>
                    {formatCurrency(sumaCobrada)}
                  </Typography>
                </Stack>
                {!cobroCompleto && (
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 1 }}>
                    <Typography variant="caption" color="warning.main">
                      Pend. de cobro
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      {formatCurrency(restante)}
                    </Typography>
                  </Stack>
                )}
              </>
            )}
              </>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};


export default FacturaPOSPagoPanel;
